import React, { useState, useEffect } from 'react';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert,
  Divider,
  List,
  ListItem,
  Snackbar,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Notifications as NotificationsIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  PhoneIphone as PushIcon,
  Event as EventIcon,
  Receipt as InvoiceIcon,
  Description as DocumentIcon,
  Message as MessageIcon,
  MedicalServices as MedicalIcon,
  Science as TestIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import clientDashboardService from '@services/clientDashboardService';

interface NotificationPreferences {
  appointments: {
    email: boolean;
    sms: boolean;
    push: boolean;
  };
  invoices: {
    email: boolean;
    sms: boolean;
    push: boolean;
  };
  documents: {
    email: boolean;
    sms: boolean;
    push: boolean;
  };
  messages: {
    email: boolean;
    sms: boolean;
    push: boolean;
  };
  medical: {
    email: boolean;
    sms: boolean;
    push: boolean;
  };
}

const ClientNotifications: React.FC = () => {
  const [preferences, setPreferences] = useState<NotificationPreferences>({
    appointments: { email: true, sms: true, push: true },
    invoices: { email: true, sms: false, push: true },
    documents: { email: true, sms: false, push: true },
    messages: { email: true, sms: false, push: true },
    medical: { email: true, sms: true, push: true }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<any>(null);

  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load notification preferences from API
      const data = await clientDashboardService.getNotificationPreferences();
      if (data) {
        setPreferences(data);
      }
    } catch (err) {
      console.error('Error loading preferences:', err);
      setError('Eroare la incarcarea preferintelor');
    } finally {
      setLoading(false);
    }
  };

  const handlePreferenceChange = async (
    category: keyof NotificationPreferences,
    channel: 'email' | 'sms' | 'push',
    value: boolean
  ) => {
    try {
      const updatedPreferences = {
        ...preferences,
        [category]: {
          ...preferences[category],
          [channel]: value
        }
      };

      setPreferences(updatedPreferences);

      // Save to API
      await clientDashboardService.updateNotificationPreferences(updatedPreferences);

      setSnackbar({ open: true, message: 'Preferinte salvate cu succes!' });
    } catch (err) {
      console.error('Error updating preferences:', err);
      setError('Eroare la salvarea preferintelor');
      setSnackbar({ open: true, message: 'Eroare la salvarea preferintelor' });
    }
  };

  const handleTestNotification = async (category: keyof NotificationPreferences) => {
    try {
      setTesting(true);
      setTestResult(null);
      
      const response = await clientDashboardService.testNotification(category, {
        title: `Test ${category}`,
        message: `Aceasta este o notificare test pentru categoria ${category}. Verifică preferințele tale!`
      });
      
      setTestResult(response);
      setSnackbar({ 
        open: true, 
        message: `Notificare test trimisă! Verifică clopotul de sus și log-urile.` 
      });
    } catch (err) {
      console.error('Error testing notification:', err);
      setSnackbar({ open: true, message: 'Eroare la trimiterea notificării test' });
    } finally {
      setTesting(false);
    }
  };

  const renderNotificationRow = (
    category: keyof NotificationPreferences,
    icon: React.ReactNode,
    title: string,
    description: string
  ) => (
    <Box key={category} sx={{ mb: 3 }}>
      <Box display="flex" alignItems="center" gap={2} mb={2}>
        {icon}
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ pl: 6 }}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <FormControlLabel
            control={
              <Switch
                checked={preferences[category].email}
                onChange={(e) => handlePreferenceChange(category, 'email', e.target.checked)}
                color="primary"
              />
            }
            label={
              <Box display="flex" alignItems="center" gap={1}>
                <EmailIcon fontSize="small" />
                <Typography variant="body2">Email</Typography>
              </Box>
            }
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 4 }}>
          <FormControlLabel
            control={
              <Switch
                checked={preferences[category].sms}
                onChange={(e) => handlePreferenceChange(category, 'sms', e.target.checked)}
                color="primary"
              />
            }
            label={
              <Box display="flex" alignItems="center" gap={1}>
                <SmsIcon fontSize="small" />
                <Typography variant="body2">SMS</Typography>
              </Box>
            }
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 4 }}>
          <FormControlLabel
            control={
              <Switch
                checked={preferences[category].push}
                onChange={(e) => handlePreferenceChange(category, 'push', e.target.checked)}
                color="primary"
              />
            }
            label={
              <Box display="flex" alignItems="center" gap={1}>
                <PushIcon fontSize="small" />
                <Typography variant="body2">Push</Typography>
              </Box>
            }
          />
        </Grid>
      </Grid>

      {/* Test Button */}
      <Box sx={{ mt: 2, pl: 6 }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<TestIcon />}
          onClick={() => handleTestNotification(category)}
          disabled={testing}
          sx={{ textTransform: 'none' }}
        >
          {testing ? 'Se trimite...' : 'Trimite notificare test'}
        </Button>
      </Box>

      <Divider sx={{ mt: 3 }} />
    </Box>
  );

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box mb={4}>
        <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
          🔔 Notificarile Mele
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Configureaza modul in care doresti sa primesti notificari
        </Typography>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Info Alert */}
      <Alert severity="info" sx={{ mb: 3 }}>
        Alege canalele de comunicare pentru fiecare tip de eveniment. Poti activa mai multe canale simultan.
      </Alert>

      {/* Notification Preferences */}
      <Card>
        <CardContent sx={{ p: 4 }}>
          {renderNotificationRow(
            'appointments',
            <EventIcon sx={{ fontSize: 40, color: 'primary.main' }} />,
            'Programari',
            'Notificari despre programari noi, confirmari si amintiri'
          )}

          {renderNotificationRow(
            'invoices',
            <InvoiceIcon sx={{ fontSize: 40, color: 'warning.main' }} />,
            'Facturi & Plati',
            'Notificari despre facturi noi, scadente si plati procesate'
          )}

          {renderNotificationRow(
            'documents',
            <DocumentIcon sx={{ fontSize: 40, color: 'info.main' }} />,
            'Documente',
            'Notificari cand sunt disponibile rezultate, retete sau alte documente'
          )}

          {renderNotificationRow(
            'messages',
            <MessageIcon sx={{ fontSize: 40, color: 'success.main' }} />,
            'Mesaje',
            'Notificari despre mesaje noi de la clinica sau medic'
          )}

          {renderNotificationRow(
            'medical',
            <MedicalIcon sx={{ fontSize: 40, color: 'error.main' }} />,
            'Medical',
            'Notificari importante despre sanatatea animalelor (vaccinari, tratamente)'
          )}
        </CardContent>
      </Card>

      {/* Test Results */}
      {testResult && (
        <Card sx={{ mt: 3, bgcolor: 'info.lighter', borderLeft: 4, borderColor: 'info.main' }}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <TestIcon color="info" />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Rezultat Test Notificare
              </Typography>
            </Box>
            
            <Alert severity="info" sx={{ mb: 2 }}>
              Notificarea a fost trimisă! Verifică clopotul de notificări din dreapta sus (ar trebui să ai +1 notificare).
            </Alert>

            <Typography variant="body2" sx={{ mb: 2 }}>
              <strong>ID Notificare:</strong> {testResult.data?.notificationId}
            </Typography>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Canale utilizate:
              </Typography>
              <List dense>
                <ListItem>
                  {testResult.data?.channels?.inApp ? '✅' : '❌'} <strong style={{marginLeft: '8px'}}>In-App:</strong> {testResult.data?.explanation?.inApp}
                </ListItem>
                <ListItem>
                  {testResult.data?.channels?.email ? '✅' : '❌'} <strong style={{marginLeft: '8px'}}>Email:</strong> {testResult.data?.explanation?.email}
                </ListItem>
                <ListItem>
                  {testResult.data?.channels?.sms ? '✅' : '❌'} <strong style={{marginLeft: '8px'}}>SMS:</strong> {testResult.data?.explanation?.sms}
                </ListItem>
                <ListItem>
                  {testResult.data?.channels?.push ? '✅' : '❌'} <strong style={{marginLeft: '8px'}}>Push:</strong> {testResult.data?.explanation?.push}
                </ListItem>
              </List>
            </Box>

            <Alert severity="success" sx={{ mt: 2 }}>
              💡 <strong>Cum să testezi:</strong> Dezactivează câteva canale mai sus (ex: Email pentru Programări), 
              salvează, apoi apasă din nou "Trimite notificare test". Vei vedea că acel canal este skip-uit!
            </Alert>
          </CardContent>
        </Card>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="success" onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ClientNotifications;

